import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFetch } from '../hooks/useFetch';
import { userAPI } from '../api/client';
import { StatCard, Card, Badge, Spinner, EmptyState, fmt, fmtDate } from '../components/UI';

const ACTION_ICONS = {
  task_completed:'✅', payment_sent:'💰', withdrawal_requested:'📤',
  withdrawal_approved:'🎉', withdrawal_rejected:'❌', earnings_added:'💵',
  account_created:'👋', login:'🔑',
};

function platformColor(name) {
  const map = { 'Appen':'#ff6b35','Scale AI':'#4f6ef7','Remotasks':'#16a34a','Toloka':'#f4a61d','Prolific':'#7c3aed','Outlier AI':'#0891b2','DataAnnotation':'#dc2626' };
  return map[name] || '#9099b4';
}

/* ── Onboarding checklist shown to pending_setup users ── */
function OnboardingChecklist({ user }) {
  const steps = [
    {
      num: 1,
      icon: '✅',
      title: 'Account Created',
      desc: 'You\'re registered on Quvera. Welcome!',
      done: true,
      locked: false,
    },
    {
      num: 2,
      icon: '💬',
      title: 'Contact Support to Get Started',
      desc: 'Reach out to our team at hello@quvera.io. We\'ll advise you on which AI platform pays best in your region and guide you through creating your account from scratch.',
      done: false,
      locked: false,
      action: (
        <a href={`mailto:hello@quvera.io?subject=Quvera Account Setup - ${encodeURIComponent(user?.name || '')}`}
          style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'10px 20px', background:'#4f6ef7', color:'#fff', borderRadius:10, fontSize:14, fontWeight:700, textDecoration:'none', marginTop:12 }}>
          📧 Email hello@quvera.io
        </a>
      ),
    },
    {
      num: 3,
      icon: '🔗',
      title: 'Submit Your Platform Account',
      desc: 'Once you\'ve created your AI platform account with our guidance, submit your credentials securely through the Platform Accounts page.',
      done: false,
      locked: true,
    },
    {
      num: 4,
      icon: '💰',
      title: 'Earnings Begin',
      desc: 'After your account is verified by our team, your dedicated professional starts working within 24 hours and your balance begins to grow.',
      done: false,
      locked: true,
    },
  ];

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif" }}>
      {/* Banner */}
      <div style={{ background:'linear-gradient(135deg,#1a1f5e,#2d3494)', borderRadius:16, padding:'24px 28px', marginBottom:24, display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
        <div style={{ width:52, height:52, borderRadius:'50%', background:'rgba(255,255,255,.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>⏳</div>
        <div style={{ flex:1 }}>
          <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:18, fontWeight:700, color:'#fff', margin:'0 0 6px' }}>
            Your account setup is in progress
          </h2>
          <p style={{ fontSize:14, color:'rgba(255,255,255,.7)', margin:0, lineHeight:1.6 }}>
            Contact our support team to get your AI platform account created and configured. We'll handle everything — just send us an email to get started.
          </p>
        </div>
        <a href="mailto:hello@quvera.io" style={{ padding:'10px 22px', background:'#fff', color:'#1a1f5e', borderRadius:10, fontSize:14, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap', flexShrink:0 }}>
          Contact Support
        </a>
      </div>

      {/* Step checklist */}
      <Card title="Your Setup Checklist">
        <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
          {steps.map((step, i) => (
            <div key={step.num} style={{ display:'flex', gap:16, padding:'20px 0', borderBottom: i < steps.length-1 ? '1px solid #f0f2f8' : 'none', opacity: step.locked ? 0.45 : 1 }}>

              {/* Step indicator */}
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:0, flexShrink:0 }}>
                <div style={{
                  width:40, height:40, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, flexShrink:0,
                  background: step.done ? '#dcfce7' : step.locked ? '#f0f2f8' : '#eef1ff',
                  color: step.done ? '#16a34a' : step.locked ? '#9099b4' : '#4f6ef7',
                  border: step.done ? '2px solid #86efac' : step.locked ? '2px solid #e2e6f0' : '2px solid #4f6ef7',
                }}>
                  {step.done ? '✓' : step.locked ? '🔒' : step.num}
                </div>
                {i < steps.length - 1 && (
                  <div style={{ width:2, flex:1, minHeight:20, background: step.done ? '#86efac' : '#e2e6f0', margin:'4px 0' }} />
                )}
              </div>

              {/* Content */}
              <div style={{ flex:1, paddingBottom: i < steps.length-1 ? 12 : 0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:15 }}>{step.icon}</span>
                  <h3 style={{ fontFamily:"'Sora',sans-serif", fontSize:15, fontWeight:700, color: step.locked?'#9099b4':'#1a1f5e', margin:0 }}>{step.title}</h3>
                  {step.done && <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background:'#dcfce7', color:'#16a34a' }}>Complete</span>}
                  {!step.done && !step.locked && <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background:'#fef9c3', color:'#b45309' }}>Action needed</span>}
                </div>
                <p style={{ fontSize:14, color: step.locked?'#b0b8ce':'#636b89', lineHeight:1.65, margin:0 }}>{step.desc}</p>
                {step.action && !step.locked}
                {step.num === 2 && (
                  <a href="mailto:hello@quvera.io" style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'9px 18px', background:'#4f6ef7', color:'#fff', borderRadius:10, fontSize:13, fontWeight:700, textDecoration:'none', marginTop:12 }}>
                    📧 Email hello@quvera.io
                  </a>
                )}
                {step.num === 3 && !step.locked && (
                  <Link to="/dashboard/platforms" style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'9px 18px', background:'#4f6ef7', color:'#fff', borderRadius:10, fontSize:13, fontWeight:700, textDecoration:'none', marginTop:12 }}>
                    Submit Account →
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Help note */}
        <div style={{ background:'#f8f9fc', borderRadius:12, padding:'14px 16px', marginTop:16, display:'flex', gap:10, alignItems:'flex-start' }}>
          <span style={{ fontSize:18 }}>💡</span>
          <div>
            <div style={{ fontSize:13, fontWeight:600, color:'#1a1f5e', marginBottom:3 }}>Not sure which platform to choose?</div>
            <div style={{ fontSize:13, color:'#636b89', lineHeight:1.6 }}>
              Our team will advise you on which AI platform pays best in your specific country and help you pass any onboarding tests. Just email us at{' '}
              <a href="mailto:hello@quvera.io" style={{ color:'#4f6ef7', fontWeight:600 }}>hello@quvera.io</a>{' '}
              to get started — we typically respond within a few hours.
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data, loading, error } = useFetch(() => userAPI.dashboard());

  if (loading) return <div style={{ padding:32 }}><Spinner /></div>;
  if (error)   return <div style={{ padding:32, color:'#dc2626' }}>{error}</div>;

  const { stats, platforms, recent_activity, pending_withdrawals } = data || {};
  const isPending = user?.onboarding_status === 'pending_setup';

  return (
    <div style={{ padding:'24px 20px', fontFamily:"'DM Sans',sans-serif", maxWidth:1200, margin:'0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontFamily:"'Sora',sans-serif", fontSize:'clamp(18px,3vw,22px)', fontWeight:700, color:'#1a1f5e', margin:0 }}>
          Welcome, {user?.name?.split(' ')[0]} 👋
        </h1>
        <p style={{ fontSize:13, color:'#9099b4', marginTop:4 }}>
          {new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'})}
        </p>
      </div>

      {/* Pending setup — show checklist instead of full dashboard */}
      {isPending ? (
        <OnboardingChecklist user={user} />
      ) : (
        <>
          {/* Pending withdrawal notice */}
          {pending_withdrawals?.length > 0 && (
            <div style={{ background:'#fef3c7', border:'1px solid #fcd34d', borderRadius:12, padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:10, fontSize:14, color:'#92400e', flexWrap:'wrap' }}>
              <span>⏳</span>
              <span>You have <strong>{pending_withdrawals.length}</strong> pending withdrawal{pending_withdrawals.length>1?'s':''} under review.</span>
              <Link to="/dashboard/withdraw" style={{ color:'#d97706', fontWeight:600, fontSize:13, marginLeft:'auto' }}>View →</Link>
            </div>
          )}

          {/* Stat cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 }} className="stat-grid-4">
            <StatCard label="Total Earnings"    value={fmt(stats?.total_earned)}     icon="💰" color="#4f6ef7"  sub="All-time" />
            <StatCard label="Total Paid Out"    value={fmt(stats?.total_paid)}        icon="✅" color="#16a34a" sub="Withdrawn" />
            <StatCard label="Available Balance" value={fmt(stats?.available_balance)} icon="🏦" color="#f4a61d" sub="Ready to withdraw" />
            <StatCard label="Pending Earnings"  value={fmt(stats?.pending_earnings)}  icon="⏳" color="#7c3aed" sub="Being processed" />
          </div>

          {/* Two col grid */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }} className="grid-2-col">
            <Card title="Active Platforms" action={<Link to="/dashboard/platforms" style={{ fontSize:13, color:'#4f6ef7', fontWeight:600, textDecoration:'none' }}>Manage →</Link>}>
              {!platforms?.length
                ? <EmptyState icon="🔗" message="No platforms yet. Submit your account details to get started." />
                : platforms.map(p => (
                    <div key={p.platform_name} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:'1px solid #f0f2f8' }}>
                      <div style={{ width:38, height:38, borderRadius:10, background: platformColor(p.platform_name), display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:12, fontWeight:700, flexShrink:0 }}>
                        {p.platform_name.slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:14, fontWeight:600, color:'#1a1f5e', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.platform_name}</div>
                        <div style={{ fontSize:12, color:'#9099b4' }}>{p.task_count} tasks</div>
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <div style={{ fontFamily:"'Sora',sans-serif", fontSize:14, fontWeight:700, color:'#16a34a' }}>{fmt(p.earned)}</div>
                        <Badge status={p.latest_status} />
                      </div>
                    </div>
                  ))
              }
            </Card>

            <Card title="Recent Activity" action={<Link to="/dashboard/activity" style={{ fontSize:13, color:'#4f6ef7', fontWeight:600, textDecoration:'none' }}>View all →</Link>}>
              {!recent_activity?.length
                ? <EmptyState icon="📭" message="No activity yet." />
                : recent_activity.slice(0,5).map(a => (
                    <div key={a.id} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'11px 0', borderBottom:'1px solid #f0f2f8' }}>
                      <div style={{ width:32, height:32, borderRadius:8, background:'#f0f2f8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>
                        {ACTION_ICONS[a.action_type]||'📌'}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:'#1a1f5e', lineHeight:1.4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.description}</div>
                        <div style={{ fontSize:11, color:'#9099b4', marginTop:2 }}>{fmtDate(a.created_at)}</div>
                      </div>
                    </div>
                  ))
              }
            </Card>
          </div>

          {/* Quick actions */}
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }} className="header-actions">
            <Link to="/dashboard/platforms" style={{ padding:'10px 18px', borderRadius:10, background:'#eef1ff', color:'#4f6ef7', fontSize:13, fontWeight:700, textDecoration:'none' }}>
              + Submit Platform Account
            </Link>
            <Link to="/dashboard/withdraw" style={{ padding:'10px 18px', borderRadius:10, background:'#4f6ef7', color:'#fff', fontSize:13, fontWeight:700, textDecoration:'none' }}>
              Withdraw Earnings
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
